import { Component, computed, inject, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { AuthButtonComponent } from '@processpuzzle/auth';
import { AppTitleComponent, DesignButtonComponent, LanguageSelectorComponent, NavMenuComponent } from '@processpuzzle/base-widget';
import { DesignRouteService } from '@processpuzzle/design';
import { ORG_ADMIN_ORG_KEY } from '@processpuzzle/org-admin';
import { LayoutService, NavigateBackComponent } from '@processpuzzle/util';
import { OrganizationSwitcherComponent } from '../organization-switcher/organization-switcher.component';

/**
 * The application bar: sidenav toggle, application title, language selector, the switcher between
 * the account's applications, design button and the sign-in button.
 *
 * The title and the handset menu are base-widget's `app-title` and `nav-menu`, reading the
 * customer's application name and the routes from the `APPLICATION_CONTEXT` this application
 * provides — see `navigation/application-context.ts`. On a handset the sidenav renders nothing, so
 * `nav-menu`, which shows itself on small screens only, stands in for it.
 */
@Component({
  selector: 'app-header',
  imports: [
    AppTitleComponent,
    AuthButtonComponent,
    DesignButtonComponent,
    LanguageSelectorComponent,
    MatIcon,
    MatIconButton,
    MatToolbar,
    NavigateBackComponent,
    NavMenuComponent,
    OrganizationSwitcherComponent,
  ],
  templateUrl: 'header.component.html',
  styleUrl: 'header.component.scss',
})
export class HeaderComponent {
  readonly layoutService = inject(LayoutService);
  /**
   * Whether the designer is open. Drives the same three things it drives in the testbed: the
   * ordinary navigation is hidden, the title changes, and `WorkspaceShellComponent` swaps the sidenav for the
   * designer's own. The design button itself stays visible in both modes — in design mode it is
   * the way back.
   */
  readonly isDesignRoute = inject(DesignRouteService).isDesignRoute;
  /**
   * Whether the URL named an organization, which is what decides that there is a designer to open.
   * Read from the same token the routes were built from rather than from the URL again, so the
   * button and the route it links to cannot disagree.
   */
  readonly hasOrganization = inject(ORG_ADMIN_ORG_KEY) !== '';
  /** `app-title`'s prop: unset, it shows the application's name from the context. */
  readonly titleOverride = computed(() => (this.isDesignRoute() ? 'Design Tools' : undefined));
  readonly toggleSideNav = output<undefined>();

  sidenavToggle() {
    this.toggleSideNav.emit(undefined);
  }
}
